import { useCallback } from "react";
import {
  ChevronLeftIcon,
  MaximizeIcon,
  PanelRightCloseIcon,
} from "lucide-react";
import { UpdateNodeContextType } from "@/app/app/_/commands";
import {
  ActivePanelNodeContextValue,
  useActivePanelNode,
} from "@/app/app/_/layouts/ActivePanelNodeContext";
import {
  detailTypenameForView,
  isTypenameValidForView,
  viewSupportsNodePanel,
} from "@/app/app/_/layouts/nodePanelConfig";
import { useAppRouteNavigation, useAppRouteState } from "@/app/app/_/routing";
import { Icon } from "@/components/atoms/Icon";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { useNode } from "@/components/organisms/Node/Node.hooks";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { useDevice } from "@/hooks/useDevice";
import { idToIdentifier, idToTypename } from "@/objects/@utils";
import { Objects } from "@/objects";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";

/**
 * Puts the panel away: the panel's own icon beside the page on a desktop, and
 * a back chevron where the panel covers the page it came from - on a phone,
 * or in a window as narrow - a bar's icon button, as the top bar's are.
 */
function NodePanelCloseButton() {
  const { isNarrow } = useDevice();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={isNarrow ? "Back" : "Close details"}
          data-testid="node-panel-close"
          onClick={() => State.getState().setIsNodePanelOpen(false)}
        >
          <Icon ReactIcon={isNarrow ? ChevronLeftIcon : PanelRightCloseIcon} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Close details</TooltipContent>
    </Tooltip>
  );
}

function NodePanelEmptyState({
  viewSupportsNode,
}: {
  viewSupportsNode: boolean;
}) {
  return (
    <StickyLayout
      stickyTopChildren={
        <NodeTitleLayout
          store={null}
          title="Node"
          isLoading={false}
          closeButton={<NodePanelCloseButton />}
        />
      }
      stickyTopClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border"
    >
      <div className="flex items-center justify-center flex-1 p-8 text-center">
        <Typography level="body-sm" className="text-muted-foreground/80">
          {viewSupportsNode
            ? "Select an item from this view to see its details here."
            : "This view doesn't have a node panel."}
        </Typography>
      </div>
    </StickyLayout>
  );
}

function NodePanelLayoutContent({
  panelNode,
  activePanelNodeId,
  activePanelAutofocus,
  typename,
}: {
  panelNode: ActivePanelNodeContextValue;
  activePanelNodeId: string;
  activePanelAutofocus: boolean;
  typename: string;
}) {
  const panelHeaderExtras = State((store) => store.panelHeaderExtras);
  const activeAppView = State((store) => store.activeAppView);
  const appRouteNavigation = useAppRouteNavigation();
  const routeState = useAppRouteState();
  const store = panelNode.store;
  const { actions, metadata } = useNode(store);

  // Views with a routed detail page open it; the rest still expand into the
  // fullscreen dialog. The typename must match the one the route encodes —
  // a panel drilled into a child object (a journal entry under transactions)
  // has no page of its own.
  const detailIdentifier =
    detailTypenameForView(activeAppView) === typename
      ? idToIdentifier(activePanelNodeId)
      : null;

  actions.push({
    label: "Fullscreen",
    icon: MaximizeIcon,
    isIconButton: true,
    onClick: () => {
      if (detailIdentifier) {
        // Close the node half on the way out — the page shows this record, and
        // the panel would repeat it. The thread half, if open, stays.
        appRouteNavigation.navigateTo({
          detailIdentifier,
          panel:
            routeState.panel === "both" || routeState.panel === "thread"
              ? "thread"
              : "none",
        });
        return;
      }
      State.getState().pushToCommandStack({
        commandDefinitionId: `update-${typename}`,
        context: {
          nodeId: activePanelNodeId,
          entityId: metadata.entityId,
          entityIdentifier: idToIdentifier(metadata.entityId),
        } as UpdateNodeContextType,
      });
    },
  });

  const effectiveTitle = panelNode.title || metadata.title || "Node";
  const handlePanelEscape = useCallback(() => {
    State.getState().setIsNodePanelOpen(false);
  }, []);

  const objectDefinition =
    Objects[typename as keyof typeof Objects] ?? undefined;
  const PanelOverride = objectDefinition?.PanelOverride;
  return (
    <StickyLayout
      stickyTopChildren={
        <NodeTitleLayout
          store={store}
          title={effectiveTitle}
          isLoading={panelNode.isLoading}
          endDecorator={panelHeaderExtras}
          actions={actions}
          closeButton={<NodePanelCloseButton />}
        />
      }
      stickyTopClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border"
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {PanelOverride ? (
          <PanelOverride
            key={activePanelNodeId}
            store={store}
            isLoading={panelNode.isLoading}
          />
        ) : (
          <NodeLayout
            key={activePanelNodeId}
            autofocus={activePanelAutofocus}
            store={store}
            isLoading={panelNode.isLoading}
            isSingleClick={true}
            isMultiline={true}
            onEscape={handlePanelEscape}
            bridge
          />
        )}
      </div>
    </StickyLayout>
  );
}

export function NodePanelLayout() {
  const activeAppView = State((store) => store.activeAppView);
  const activePanelNodeId = State((store) => store.activePanelNodeId);
  const activePanelAutofocus = State((store) => store.activePanelAutofocus);
  const panelNode = useActivePanelNode();
  const typename = idToTypename(activePanelNodeId);
  const objectDefinition =
    typename != null ? Objects[typename as keyof typeof Objects] : undefined;

  const viewSupportsNode = viewSupportsNodePanel(activeAppView);
  const idIsValidForView = isTypenameValidForView(activeAppView, typename);

  if (!viewSupportsNode || !activePanelNodeId || !idIsValidForView) {
    return <NodePanelEmptyState viewSupportsNode={viewSupportsNode} />;
  }

  // Switched to another record, the store is the last one's until the new
  // one's loader hands its own over: drawn from it, the new record would open
  // on tabs worked out from the last.
  const storeNodeId = panelNode?.store?.getState().fieldValues.id;
  if (
    !panelNode?.store ||
    (storeNodeId != null && storeNodeId !== activePanelNodeId)
  ) {
    const title =
      panelNode?.title ||
      objectDefinition?.getTitle?.({
        __typename: typename,
        id: activePanelNodeId,
      } as never) ||
      objectDefinition?.humanName ||
      "Node";
    return (
      <StickyLayout
        stickyTopChildren={
          <NodeTitleLayout
            store={null}
            title={title}
            isLoading={true}
            closeButton={<NodePanelCloseButton />}
          />
        }
        stickyTopClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border"
      >
        <div className="flex-1 min-h-30" />
      </StickyLayout>
    );
  }

  return (
    <NodePanelLayoutContent
      panelNode={panelNode}
      activePanelNodeId={activePanelNodeId}
      activePanelAutofocus={activePanelAutofocus}
      typename={typename}
    />
  );
}
